import {
  commitResourceFiber,
  createResourceFiber,
  renderResourceFiber,
  unmountResourceFiber,
} from "./core/ResourceFiber";
import { LifecycleChild, LifecycleHost } from "./core/lifecycle";
import { UpdateScheduler } from "./core/scheduler";
import { tapConst } from "./hooks/tap-const";
import { tapMemo } from "./hooks/tap-memo";
import { tapEffect } from "./hooks/tap-effect";
import { tapEffectEvent } from "./hooks/tap-effect-event";
import { tapRef } from "./hooks/tap-ref";
import type { RenderResult, ResourceElement } from "./core/types";
import { isDevelopment } from "./core/helpers/env";
import {
  commitRoot,
  createResourceFiberRoot,
  setRootVersion,
} from "./core/helpers/root";

export namespace tapResourceRoot {
  export type Unsubscribe = () => void;

  export interface SubscribableResource<TState> {
    /**
     * Get the current state of the store.
     */
    getValue(): TState;

    /**
     * Subscribe to the store.
     */
    subscribe(listener: () => void): Unsubscribe;
  }
}

export const tapResourceRoot = <TState>(
  element: ResourceElement<TState>,
  host?: LifecycleHost,
): tapResourceRoot.SubscribableResource<TState> => {
  const scheduler = tapConst(
    () =>
      host ? null : new UpdateScheduler(() => handleScheduledUpdate(null)),
    [],
  );
  const queue = tapConst(() => [] as (() => void)[], []);

  const fiber = tapMemo(() => {
    void element.key;

    return createResourceFiber(
      element.type,
      createResourceFiberRoot((callback) => {
        if (host) {
          host.dispatch(callback);
          return;
        }

        if (!scheduler!.isDirty && !callback()) return;
        queue.push(callback);
        scheduler!.markDirty();
      }),
    );
  }, [element.type, element.key, host, scheduler]);

  setRootVersion(fiber.root, fiber.root.committedVersion);
  const render = renderResourceFiber(fiber, element.props);

  const isMountedRef = tapRef(false);
  const committedPropsRef = tapRef(element.props);
  const valueRef = tapRef<TState>(render.output);
  const subscribers = tapConst(() => new Set<() => void>(), []);
  const handleScheduledUpdate = tapEffectEvent(
    (render: RenderResult | null) => {
      if (host) {
        throw new Error("Scheduled update received for a hosted resource root");
      }

      if (render === null) {
        setRootVersion(fiber.root, 2);
        setRootVersion(fiber.root, 1);

        queue.forEach((callback) => {
          if (isDevelopment && fiber.devStrictMode) {
            callback();
          }

          callback();
        });

        if (isDevelopment && fiber.devStrictMode) {
          void renderResourceFiber(fiber, committedPropsRef.current);
        }

        render = renderResourceFiber(fiber, committedPropsRef.current);
      }

      if (scheduler!.isDirty)
        throw new Error("Scheduler is dirty, this should never happen");

      commitRoot(fiber.root);
      queue.length = 0;

      if (isMountedRef.current) {
        commitResourceFiber(fiber, render);
      }

      if (scheduler!.isDirty || valueRef.current === render.output) return;
      valueRef.current = render.output;
      subscribers.forEach((callback) => callback());
    },
  );

  const child: LifecycleChild = tapMemo(
    () => ({
      devStrictMode: isDevelopment && !!fiber.devStrictMode,
      setVersion: (v: number) => setRootVersion(fiber.root, v),
      render: () => renderResourceFiber(fiber, committedPropsRef.current),
      commit: (r: RenderResult) => {
        commitRoot(fiber.root);
        commitResourceFiber(fiber, r);
        if (valueRef.current !== r.output) {
          valueRef.current = r.output;
          subscribers.forEach((callback) => callback());
        }
      },
    }),
    [fiber],
  );

  tapEffect(() => {
    isMountedRef.current = true;
    if (host) {
      host.mount(child);
    }
    return () => {
      isMountedRef.current = false;
      if (host) {
        host.unmount();
      }
      unmountResourceFiber(fiber);
    };
  }, [fiber, child, host]);

  tapEffect(() => {
    committedPropsRef.current = render.props;
    commitRoot(fiber.root);
    commitResourceFiber(fiber, render);

    if ((!host && scheduler!.isDirty) || valueRef.current === render.output)
      return;
    valueRef.current = render.output;
    subscribers.forEach((callback) => callback());
  });

  return tapMemo(
    () => ({
      getValue: () => valueRef.current,
      subscribe: (listener: () => void) => {
        subscribers.add(listener);
        return () => subscribers.delete(listener);
      },
    }),
    [],
  );
};
